import { router } from 'expo-router';
import type { ReactNode } from 'react';
import { Text, type StyleProp, type TextStyle } from 'react-native';

import type { ProfileMention } from '@/src/features/posts/post-service';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { openUserProfile } from '@/src/utils/profile-navigation';

import { extractTextLinks, openTextLink, type TextLinkMatch } from './link-utils';
import {
  profileMentionsForText,
  type ProfileMentionLike,
} from './profile-mention-helpers';

function mentionAtIndex(
  body: string,
  mentions: ProfileMentionLike[],
  index: number,
) {
  return mentions
    .filter((mention) => body.startsWith(mention.label, index))
    .sort((first, second) => second.label.length - first.label.length)[0];
}

export function ProfileMentionText({
  body,
  currentUid,
  linkStyle,
  mentionStyle,
  mentions,
  numberOfLines,
  source,
  style,
}: {
  body: string;
  currentUid?: string;
  linkStyle?: StyleProp<TextStyle>;
  mentionStyle?: StyleProp<TextStyle>;
  mentions: ProfileMention[];
  numberOfLines?: number;
  source: string;
  style: StyleProp<TextStyle>;
}) {
  const validMentions = profileMentionsForText(body, mentions);
  const validLinks = extractTextLinks(body);

  if (validMentions.length === 0 && validLinks.length === 0) {
    return (
      <Text numberOfLines={numberOfLines} style={style}>
        {body}
      </Text>
    );
  }

  const children: ReactNode[] = [];
  let cursor = 0;

  while (cursor < body.length) {
    let nextMention: ProfileMention | undefined;
    let nextIndex = -1;
    let nextLink: TextLinkMatch | undefined;

    validMentions.forEach((mention) => {
      const foundIndex = body.indexOf(mention.label, cursor);

      if (foundIndex < 0) {
        return;
      }

      if (
        nextIndex < 0 ||
        foundIndex < nextIndex ||
        (foundIndex === nextIndex &&
          mention.label.length > (nextMention?.label.length ?? 0))
      ) {
        nextMention = mention;
        nextIndex = foundIndex;
      }
    });

    nextLink = validLinks.find((link) => link.start >= cursor);

    if (
      nextLink &&
      (nextIndex < 0 ||
        nextLink.start < nextIndex ||
        (nextLink.start === nextIndex &&
          nextLink.displayText.length > (nextMention?.label.length ?? 0)))
    ) {
      if (nextLink.start > cursor) {
        children.push(body.slice(cursor, nextLink.start));
      }

      children.push(
        <Text
          key={`link-${nextLink.start}-${nextLink.url}`}
          accessibilityRole="link"
          onPress={(event) => {
            event.stopPropagation();
            void openTextLink({
              source,
              url: nextLink.url,
            });
          }}
          style={[defaultStyles.link, linkStyle]}
        >
          {nextLink.displayText}
        </Text>,
      );
      cursor = nextLink.end;
      continue;
    }

    if (!nextMention || nextIndex < 0) {
      children.push(body.slice(cursor));
      break;
    }

    if (nextIndex > cursor) {
      children.push(body.slice(cursor, nextIndex));
    }

    const mention = mentionAtIndex(body, validMentions, nextIndex) ?? nextMention;

    children.push(
      <Text
        key={`${mention.uid}-${nextIndex}`}
        accessibilityRole="link"
        onPress={(event) => {
          event.stopPropagation();

          if (!currentUid) {
            router.push('/sign-in');
            return;
          }

          trackEvent('profile_open', {
            source,
            target_is_current_user: mention.uid === currentUid,
          });
          openUserProfile({ currentUid, targetUid: mention.uid });
        }}
        style={[defaultStyles.mention, mentionStyle]}
      >
        {mention.label}
      </Text>,
    );
    cursor = nextIndex + mention.label.length;
  }

  return (
    <Text numberOfLines={numberOfLines} style={style}>
      {children}
    </Text>
  );
}

const defaultStyles = {
  link: {
    color: colors.primary,
    fontWeight: '800' as const,
    textDecorationLine: 'underline' as const,
    textDecorationColor: colors.primary,
  },
  mention: {
    color: colors.primary,
    fontWeight: '900' as const,
  },
};
